export const STRUCTURAL_RANGE_LAYER_SCHEMA_VERSION = 'variorum.structural-range-layer.v1' as const
export const STRUCTURAL_RANGE_TAXONOMY_VERSION = 2 as const

// Preferred editorial vocabulary. The stored `discourse` kind is displayed as
// Dialogue, so existing records and older clients keep the same value.
export const EDITORIAL_STRUCTURAL_RANGE_KINDS = [
  'frame_dialogue', 'subframe', 'scene', 'discourse', 'monologue',
  'subtale', 'tertiary_tale', 'hikma', 'mathal', 'narrative', 'divider',
] as const
export const LEGACY_STRUCTURAL_RANGE_KINDS = ['frame', 'tale', 'transition'] as const

export const STRUCTURAL_RANGE_KINDS = [
  ...EDITORIAL_STRUCTURAL_RANGE_KINDS,
  ...LEGACY_STRUCTURAL_RANGE_KINDS,
] as const

export type StructuralRangeKind = typeof STRUCTURAL_RANGE_KINDS[number]
export type StructuralRangeStatus = 'unclassified' | 'suggested' | 'confirmed' | 'rejected'
export type StructuralRangeOrigin = 'human' | 'model' | 'import' | 'migration'

export interface StructuralRangeProvenance {
  sourceKind?: 'html-sidecar' | 'legacy-row-scene' | 'pi-structured-generation'
  sourceName?: string
  sourceHash?: string
  sourceRangeId?: string
  generationId?: string
  provider?: string
  modelId?: string
}

export interface StructuralRange {
  id: string
  title: string
  kind: StructuralRangeKind | null
  startRowId: string
  endRowId: string
  parentRangeId: string | null
  status: StructuralRangeStatus
  origin: StructuralRangeOrigin
  confidence?: number | null
  notes?: string
  provenance?: StructuralRangeProvenance
  createdAt: string
  updatedAt: string
  updatedBy?: string
}

export interface ChapterStructuralRangeLayer {
  schemaVersion: typeof STRUCTURAL_RANGE_LAYER_SCHEMA_VERSION
  taxonomyVersion: 1 | typeof STRUCTURAL_RANGE_TAXONOMY_VERSION
  id: string
  chapterId: string
  bookId?: string
  version: number
  ranges: StructuralRange[]
  createdAt: string
  updatedAt: string
}

export interface StructuralRangeDiagnostic {
  severity: 'error' | 'warning'
  code:
    | 'missing-row'
    | 'reversed-range'
    | 'missing-parent'
    | 'parent-does-not-enclose'
    | 'crossing-ranges'
    | 'invalid-parent-kind'
    | 'transition-has-children'
    | 'cycle'
    | 'unclassified'
    | 'invalid-kind'
    | 'unexpected-root'
  message: string
  rangeId: string
  relatedRangeId?: string
}

export interface StructuralRangeContextEntry {
  id: string
  title: string
  kind: StructuralRangeKind
  startRowId: string
  endRowId: string
  parentRangeId: string | null
  depth: number
  narrativeLevel: number
}

export interface StructuralRangeContext {
  rowId: string
  activeRangePath: StructuralRangeContextEntry[]
  immediateParent: StructuralRangeContextEntry | null
  children: StructuralRangeContextEntry[]
  previousSibling: StructuralRangeContextEntry | null
  nextSibling: StructuralRangeContextEntry | null
}
